// Helpers for the redesigned UnenrollDialog.
//
// Extracted out of UnenrollDialog.tsx so the main file stays under the
// review-friendly ~600 line budget. Everything here is pure (no React,
// no Firestore, no Redux) so it's straightforward to unit-test in
// isolation and to reason about.
//
// The deposit-candidate ranking, money math, and date helpers all
// mirror the redesign mock in public/unenroll-redesign.html one-to-one
// so director + parent + design preview agree on the numbers.

import type { StudentType } from '@wonderschool/common-base-types';
import type { EnrollmentDeposit } from '../../../enrollments/types';
import { BIWEEKLY_DIVISOR, TWICE_PER_MONTH_DIVISOR, WEEKLY_DIVISOR } from '../../../helpers/invoicePlans';
import { logError } from '../../../rollbar';

// ----- Money formatting + math --------------------------------------------

export const fmtCents = (cents: number | undefined): string => '$' + ((cents || 0) / 100).toFixed(2);

// Annual-average week-to-month conversion. 52 weeks/yr ÷ 12 months = 4.333,
// which the handbook rounds to 4.3. Multiplied by 7 days that lands on
// 30.1 days/month — closer to the 365-day-year actual than the old 30
// basis (which had a ~1.4% annual upward bias).
export const WEEKS_PER_MONTH = 4.3;
export const DAYS_PER_MONTH = WEEKS_PER_MONTH * 7; // 30.1

// Days per billing period by interval — used to compute daily rate directly
// from per-period amounts, bypassing the WEEKLY_DIVISOR→DAYS_PER_MONTH round-trip
// that introduces a ~0.7% overcharge for weekly and bi-weekly providers.
const DAYS_IN_INTERVAL: Record<string, number> = {
  weekly: 7,
  'bi-weekly': 14,
  monthly: DAYS_PER_MONTH,
  'twice per month': DAYS_PER_MONTH / 2,
};

function startOfDayUTC(d: Date): Date {
  // UTC accessors are correct because all callers pass UTC-midnight values:
  // - noticeGivenOn / "today" are always schoolTimezoneUtcMidnight output (Date.UTC)
  // - lastDay in the submit path is toDateObject() → UTC midnight
  // - lastDay in the preview path is `T00:00:00` local midnight; for US
  //   negative-offset zones (UTC-4..UTC-10) getUTCDate() == getDate() because
  //   local midnight is later than UTC midnight on the same calendar day
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}

/**
 * Returns UTC midnight of "today" in the school's IANA timezone. Writing this
 * as `noticeGivenOn` means both the form (UTC accessors in startOfDayUTC) and
 * the cron (getUTC* in startOfDayUtcMs) read the same calendar day, regardless
 * of when or where the director files.
 *
 * Falls back to UTC midnight of the browser's local calendar day when no school
 * timezone is available.
 */
export function schoolTimezoneUtcMidnight(schoolTz: string | undefined): Date {
  const now = new Date();
  if (!schoolTz) {
    logError('schoolTimezoneUtcMidnight: no school timezone — falling back to browser local date', null, {
      context: 'unenrollHelpers',
    });
  }
  const tz = schoolTz || Intl.DateTimeFormat().resolvedOptions().timeZone;
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
      .formatToParts(now)
      .reduce((acc, p) => ({ ...acc, [p.type]: Number(p.value) }), {} as Record<string, number>);
    return new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
  } catch (err) {
    logError('schoolTimezoneUtcMidnight: Intl.DateTimeFormat failed — falling back to browser local date', err, {
      schoolTz,
    });
    return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
  }
}

export function daysBetween(from: Date, to: Date): number {
  const fMs = startOfDayUTC(from).getTime();
  const tMs = startOfDayUTC(to).getTime();
  return Math.max(0, Math.floor((tMs - fMs) / 86400000));
}

export type ProrationResult = {
  dailyCents: number;
  daysNotice: number;
  minNoticeDays: number;
  prorateDays: number;
  prorateAmountCents: number;
  depositHeldCents: number;
  depositCreditCents: number;
  netOwedCents: number;
  meetsNotice: boolean;
  willCharge: boolean;
  willRefund: boolean;
  // True when the director picked a last-day-enrolled in the past.
  // The math still computes (treating notice as 0), but the dialog
  // surfaces a confirmation so this isn't a silent maxed-prorate.
  isPastDated: boolean;
  daysPastDated: number;
};

/**
 * Prorate math for an unenrollment. Mirrors the calcProration helper
 * in src/enrollments/components/pages/StartUnenrollmentPage.tsx so
 * the director-facing modal and the parent-facing confirmation page
 * agree on the numbers.
 *
 *   prorated_amount = max(0, minNoticeDays - daysNotice) × dailyTuitionCents
 *
 * Callers are responsible for computing the daily rate from the plan's
 * billing interval directly (see `computeDailyTuitionCents`), bypassing
 * the monthly-normalization round-trip that introduced a ~0.7% overcharge
 * for weekly and bi-weekly providers (CCMS-8118).
 *
 * Round at the end, not after the daily-rate computation, so the
 * compounding $0.10-on-30-days error disappears. `dailyCents` is
 * still computed for display only (`X days × $Y/day` line).
 *
 * `depositHeldCents` should be 0 for non-refundable deposits — callers
 * pre-zero it so this stays a pure math fn that doesn't know about
 * the refundable flag.
 */
export function calcProration(
  dailyTuitionCents: number,
  minNoticeDays: number,
  depositHeldCents: number,
  lastDayStr: string,
  /**
   * When notice was given. Pass `schoolTimezoneUtcMidnight(primaryLocation.timezone)`
   * on new submissions so the date is anchored to the school's calendar day (not the
   * director's browser clock). Pass `enrollment.unenrolled.noticeGivenOn` on read
   * paths (dashboard, re-submit) so the calculation matches what the cron will charge.
   */
  noticeGivenDate?: Date,
  /**
   * The "now" anchor used for `isPastDated` detection and as the `noticeGivenDate`
   * fallback. Pass `schoolTimezoneUtcMidnight(primaryLocation.timezone)` on new
   * submissions so both the live preview and the cron agree on what "today" is.
   * Defaults to `new Date()` when omitted (tests control this via `vi.useFakeTimers`).
   */
  today?: Date
): ProrationResult {
  const anchor = today ?? new Date();
  // Parse as local midnight — ISO-only strings (no time component) are
  // treated as UTC by the JS spec, which shifts the date back one day
  // in negative-offset timezones (e.g. PDT). Appending T00:00:00 forces
  // local-time parsing so the day the director picked is the day used.
  const lastDay = lastDayStr ? new Date(lastDayStr + 'T00:00:00') : new Date();

  // Detect past-dated unenroll BEFORE `daysBetween` floors at zero, so
  // the dialog can surface a confirmation.
  const rawGapMs = startOfDayUTC(lastDay).getTime() - startOfDayUTC(anchor).getTime();
  const isPastDated = rawGapMs < 0;
  const daysPastDated = isPastDated ? Math.ceil(-rawGapMs / 86400000) : 0;

  const daysNotice = daysBetween(noticeGivenDate ?? anchor, lastDay);
  const prorateDays = Math.max(0, minNoticeDays - daysNotice);

  // Compute prorate in high precision, round at the end.
  const daily = dailyTuitionCents || 0;
  const prorateAmountCents = Math.round(prorateDays * daily);
  // dailyCents is for display only — surfaces as "X days × $Y/day".
  const dailyCents = Math.round(daily);

  const depositCreditCents = Math.min(depositHeldCents, prorateAmountCents);
  const netOwedCents = prorateAmountCents - depositHeldCents;
  return {
    dailyCents,
    daysNotice,
    minNoticeDays,
    prorateDays,
    prorateAmountCents,
    depositHeldCents,
    depositCreditCents,
    netOwedCents,
    meetsNotice: daysNotice >= minNoticeDays,
    willCharge: netOwedCents > 0,
    willRefund: netOwedCents < 0,
    isPastDated,
    daysPastDated,
  };
}

/**
 * Notice-period fields for the `enrollment.unenrolled` write, keyed off what
 * the parent handbook actually provided. Both values are optional because the
 * handbook lookup can fail (missing marketplaceSchoolId) or the provider may
 * simply have no minNoticeDays configured — "no policy" is expressed by
 * omitting the keys entirely, never by writing `undefined`: the legacy
 * api/firebase app does not set ignoreUndefinedProperties, so an undefined
 * field value makes batch.set() throw and the whole unenrollment fail
 * (CCMS-8186). Zero is a real value and is kept.
 */
export function unenrolledNoticeFields(
  noticeDays: number | undefined,
  handbookNoticeDays: number | undefined
): { noticeDaysApplied?: number; noticeDaysFromHandbook?: number } {
  return {
    ...(noticeDays !== undefined && { noticeDaysApplied: noticeDays }),
    ...(handbookNoticeDays !== undefined && { noticeDaysFromHandbook: handbookNoticeDays }),
  };
}

export function nextFirstOfMonthAfter(date: Date): Date {
  const d = new Date(date);
  const day = d.getDate();
  const monthOffset = day === 1 ? 0 : 1;
  return new Date(d.getFullYear(), d.getMonth() + monthOffset + 1, 1);
}

/**
 * Compute when the next recurring invoice will hit the family for a
 * given set of selected plans. Reads `plan.chargeDate` (preferred) or
 * `plan.dateDue` from each plan; if multiple plans have charge dates,
 * returns the earliest one after `lastDay` (that's the next time the
 * family will see money). Falls back to `nextFirstOfMonthAfter` only
 * when no plan exposes either field — that's the "we don't know what
 * the school's billing cycle is" case.
 */
export function nextChargeDate(lastDay: Date, plans: Array<{ chargeDate?: number; dateDue?: number }>): Date {
  const lastDayMs = lastDay.getTime();
  let earliest: Date | null = null;
  for (const p of plans || []) {
    const candidateMs = p.chargeDate || p.dateDue;
    if (!candidateMs || candidateMs <= lastDayMs) continue;
    const d = new Date(candidateMs);
    if (!earliest || d < earliest) earliest = d;
  }
  return earliest || nextFirstOfMonthAfter(lastDay);
}

export function fmtShortDate(date: Date): string {
  // timeZone: 'UTC' keeps the displayed date aligned with what the director
  // entered — dates from Firestore are stored as midnight UTC, so without
  // this a browser in a negative-offset timezone (CST, PDT) would display
  // the previous calendar day.
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

// ----- Parent / student name helpers -------------------------------------

/**
 * Pull a parent's name out of `student.family` — scans the {uid → contact}
 * dict for the first entry with relationship === 'parent', then falls
 * back to the first entry that at least has a name. Mirrors the
 * extractParentName helper in the redesign mock.
 */
export function extractParentName(student: Partial<StudentType> | null | undefined): string {
  if (!student) return '-';
  const family = (student as any).family || {};
  const entries = Object.values(family) as Array<{
    firstName?: string;
    lastName?: string;
    relationship?: string;
    email?: string;
  }>;
  const parent =
    entries.find((m) => (m?.relationship || '').toLowerCase() === 'parent') || entries.find((m) => m?.firstName);
  if (!parent) return '-';
  const name = `${parent.firstName || ''} ${parent.lastName || ''}`.trim();
  return name || parent.email || '-';
}

export function studentFullName(student: Partial<StudentType> | null | undefined): string {
  if (!student) return '';
  const f = (student as any).firstName || '';
  const l = (student as any).lastName || '';
  return `${f} ${l}`.trim();
}

// ----- Deposit candidate resolver ----------------------------------------

export type DepositCandidate = {
  id: string;
  source: string;
  label: string;
  amountCents: number;
  refundable: boolean;
  score: number;
  note: string;
  href?: string;
  linkLabel?: string;
};

const DEPOSIT_FEE_REGEX = /deposit|(enrollment|registration).*fee/i;
const NON_REFUNDABLE_REGEX = /non-?refundable/i;

/**
 * Build the list of deposit-source candidates for a student. Ranks:
 *   90  structured `enrollment.deposit` (non-refunded)
 *   70  legacy fee in tuitionAndFees.fees[] matching the deposit-name regex
 *   10  manual override (added by the picker UI, not here)
 *    0  none
 *
 * Used to populate the deposit picker. Best-scored candidate is the
 * auto-selected default.
 */
export function buildDepositCandidates(student: Partial<StudentType> | null | undefined): DepositCandidate[] {
  const candidates: DepositCandidate[] = [];
  if (!student) return candidates;
  const studentId = (student as any).id;
  const deposit: EnrollmentDeposit | undefined = (student as any).enrollment?.deposit;
  if (deposit && typeof deposit.amountCents === 'number' && !deposit.settledAt) {
    const isRefundable = deposit.refundable !== false;
    candidates.push({
      id: 'structured',
      source: 'school record',
      label: `Deposit on school record · ${fmtCents(deposit.amountCents)} (${
        isRefundable ? 'goes back to family' : 'school keeps'
      })`,
      amountCents: deposit.amountCents,
      refundable: isRefundable,
      score: 90,
      note: 'This is the official deposit record for the family.',
      href: `/student/${studentId}#deposit`,
      linkLabel: 'Open student profile',
    });
  }
  const fees: Array<{ name?: string; amount?: number }> = (student as any).enrollment?.tuitionAndFees?.fees ?? [];
  fees.forEach((f, i) => {
    if (DEPOSIT_FEE_REGEX.test(f.name || '')) {
      // Skip if the structured deposit is already settled — it was credited during a
      // prior unenroll and offering this legacy fee again risks a double-credit.
      if (deposit?.settledAt) return;
      const isRefundable = !NON_REFUNDABLE_REGEX.test(f.name || '');
      candidates.push({
        id: `fee_${i}`,
        source: 'old fee list',
        label: `"${f.name}" · ${fmtCents(f.amount || 0)} (${isRefundable ? 'goes back to family' : 'school keeps'})`,
        amountCents: f.amount || 0,
        refundable: isRefundable,
        score: 70,
        note: `This deposit is on the family's old fee list. We'll move it to the official deposit record when you save.`,
        href: `/student/${studentId}/invoices#one-time-fees`,
        linkLabel: 'Open student invoices',
      });
    }
  });
  return candidates;
}

export function bestDepositCandidate(candidates: DepositCandidate[]): DepositCandidate | null {
  if (!candidates || candidates.length === 0) return null;
  return [...candidates].sort((a, b) => b.score - a.score)[0];
}

// ----- Tuition resolver --------------------------------------------------

/**
 * Best-guess monthly tuition for the student, in cents. Looks at the
 * explicit `enrollment.tuitionAndFees.tuition.amount` first, then falls
 * back to the sum of the student's active monthly plans (so a student
 * with three monthly plans of $X, $Y, $Z resolves as $X+$Y+$Z).
 */
export function guessMonthlyTuitionCents(
  student: Partial<StudentType> | null | undefined,
  allPlans: Array<{
    id: string;
    total?: number; // InvoicePlan.total — dollars (e.g. 2300 = $2,300)
    billingInterval?: string; // InvoicePlan.billingInterval (e.g. "Monthly")
    students?: Array<{ id?: string }>;
  }>
): number {
  // Each per-invoice amount is converted to a monthly equivalent using the
  // inverse of the divisor applied at plan creation time. Keys are lowercase
  // so they match both BillingIntervalEnum ('bi-weekly') and InvoicePlan
  // ('Bi-Weekly') after a .toLowerCase() call.
  const MONTHLY_MULTIPLIER: Record<string, number> = {
    weekly: WEEKLY_DIVISOR,
    'bi-weekly': BIWEEKLY_DIVISOR,
    'twice per month': TWICE_PER_MONTH_DIVISOR,
    monthly: 1,
  };

  // Prefer the explicit tuition field. tuition.amount is a per-invoice amount
  // (same convention as InvoicePlan.total), so the same multiplier applies.
  const tuition = (student as any)?.enrollment?.tuitionAndFees?.tuition;
  if (tuition && typeof tuition.amount === 'number' && tuition.amount > 0) {
    const interval = (tuition.billingInterval ?? '').toLowerCase();
    const multiplier = MONTHLY_MULTIPLIER[interval];
    if (!interval || multiplier == null) {
      logError('guessMonthlyTuitionCents: unknown tuition billing interval', null, { interval });
      return 0;
    }
    return Math.round(tuition.amount * multiplier);
  }

  if (!student) return 0;
  const sid = (student as any).id;
  return allPlans
    .filter((p) => (p.students || []).some((s) => s.id === sid))
    .reduce((sum, p) => {
      if (!p.billingInterval) return sum;
      const multiplier = MONTHLY_MULTIPLIER[p.billingInterval.toLowerCase()];
      if (multiplier == null) {
        logError('guessMonthlyTuitionCents: unknown billing interval in plan', null, {
          planId: p.id,
          billingInterval: p.billingInterval,
        });
        return sum;
      }
      return sum + Math.round((p.total || 0) * 100 * multiplier);
    }, 0);
}

/**
 * Compute the daily tuition rate in cents from a student's active plans.
 * Sums per-plan daily rates computed directly from each plan's billing
 * interval, bypassing the monthly-normalization round-trip that introduces
 * a ~0.7% overcharge for weekly and bi-weekly providers.
 */
export function computeDailyTuitionCents(
  allPlans: Array<{
    id: string;
    total?: number;
    billingInterval?: string;
    students?: Array<{ id?: string }>;
  }>,
  studentId: string
): number {
  if (!studentId) return 0;
  return allPlans
    .filter((p) => p.students?.some((s) => s.id === studentId))
    .reduce((sum, p) => {
      if (!p.billingInterval) return sum;
      const days = DAYS_IN_INTERVAL[p.billingInterval.toLowerCase()];
      if (!days) {
        logError('computeDailyTuitionCents: unknown billing interval', null, {
          planId: p.id,
          billingInterval: p.billingInterval,
        });
        return sum;
      }
      return sum + ((p.total || 0) * 100) / days;
    }, 0);
}

// ----- Plan label --------------------------------------------------------

/**
 * Build the "Parent · Child · $X/month — Plan name" label shown in
 * each plan-picker dropdown. The plan's first student is used as the
 * source of the parent/child names; if the linked student doc isn't in
 * the studentsById map (e.g. the family is on another student doc),
 * we degrade gracefully to the generic placeholders.
 */
export function planLabelFor(
  plan: {
    id: string;
    name?: string;
    total?: number; // InvoicePlan.total — dollars (e.g. 2300 = $2,300)
    billingInterval?: string; // InvoicePlan.billingInterval (e.g. "Monthly")
    students?: Array<{ id?: string }>;
  },
  studentsById: Record<string, Partial<StudentType>>
): string {
  const linkedId = plan.students?.[0]?.id;
  const linked = linkedId ? studentsById[linkedId] : undefined;
  const childName = linked ? studentFullName(linked) || '(child name not on record)' : '(child name not on record)';
  const parent = extractParentName(linked);
  const interval = (plan.billingInterval || 'monthly').toLowerCase();
  // InvoicePlan.total is in dollars; convert to cents for fmtCents.
  const amountCents = Math.round((plan.total || 0) * 100);
  const base = `${parent} · ${childName} · ${fmtCents(amountCents)} every ${interval.replace(/ly$/, '')}`;
  // Only append a plan name if it's a real, human-given name. Hide the
  // "Plan abc123…" code-fallback — that just adds visual noise for the
  // provider who doesn't know what those characters mean.
  return plan.name ? `${base} — ${plan.name}` : base;
}
